import { useState } from 'react'
import { cn } from '@/utils'
import type { DisplaySource } from '@/types/source'
import { Card, CardContent } from '@/components/ui'
import { Badge } from '@/components/ui'
import { Button } from '@/components/ui'
import {
  FileText,
  Video,
  Headphones,
  Image,
  BookOpen,
  Globe,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Clock,
} from 'lucide-react'

interface SourcePanelProps {
  sources: DisplaySource[]
  className?: string
  onSourceClick?: (source: DisplaySource) => void
}

export function SourcePanel({ sources, className, onSourceClick }: SourcePanelProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  if (sources.length === 0) {
    return null
  }

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <FileText className="w-4 h-4" />
        <span>Sources ({sources.length})</span>
      </div>

      <div className="space-y-2">
        {sources.map((source, index) => (
          <SourceCard
            key={source.id}
            source={source}
            index={index + 1}
            isExpanded={expandedIds.has(source.id)}
            onToggle={() => toggleExpanded(source.id)}
            onClick={onSourceClick ? () => onSourceClick(source) : undefined}
          />
        ))}
      </div>
    </div>
  )
}

interface SourceCardProps {
  source: DisplaySource
  index: number
  isExpanded: boolean
  onToggle: () => void
  onClick?: () => void
}

const mediaIcons = {
  document: FileText,
  video: Video,
  audio: Headphones,
  image: Image,
  web: Globe,
  scorm: BookOpen,
}

function SourceCard({ source, index, isExpanded, onToggle, onClick }: SourceCardProps) {
  const [copied, setCopied] = useState(false)

  const copyContent = async () => {
    await navigator.clipboard.writeText(source.document)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const Icon = mediaIcons[source.mediaType]
  const scoreColor =
    source.scorePercentage >= 80
      ? 'text-success'
      : source.scorePercentage >= 60
        ? 'text-warning-strong'
        : 'text-muted-foreground'

  return (
    <Card className="overflow-hidden gap-0">
      {/* Header - Always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-muted transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Index badge */}
          <div className="shrink-0 w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center">
            <span className="text-xs font-medium text-primary">
              {index}
            </span>
          </div>
          <div className="shrink-0 w-8 h-8 rounded-sm bg-muted flex items-center justify-center">
            <Icon className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <h4 className="font-medium text-foreground truncate text-sm">
              {source.title}
            </h4>
            {source.formattedTimeRange && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>{source.formattedTimeRange}</span>
              </div>
            )}
            {source.formattedPageNumber && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <BookOpen className="w-3 h-3" />
                <span>{source.formattedPageNumber}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className={cn('text-xs', scoreColor)}
          >
            {source.scorePercentage}%
          </Badge>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <CardContent className="pt-0 pb-3 px-3 border-t border-border">
          <div className="mt-3 space-y-3">
            {/* Content preview */}
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm text-foreground whitespace-pre-wrap line-clamp-6">
                {source.document}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    copyContent()
                  }}
                  leftIcon={
                    copied ? (
                      <Check className="w-3 h-3 text-success" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )
                  }
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                {onClick && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      onClick()
                    }}
                  >
                    View
                  </Button>
                )}
              </div>

              {/* Media type badge */}
              <Badge variant="secondary" className="text-xs capitalize">
                {source.mediaType}
              </Badge>
            </div>

            {/* Expiration warning */}
            {source.isExpired && (
              <div className="text-xs text-warning-strong flex items-center gap-1">
                <span>Link expired - content preview only</span>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  )
}
